/**
 * Performance Profiling Utility
 * Tracks and logs performance metrics for debugging and optimization
 */

interface PerformanceMarker {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  metadata?: Record<string, any>;
}

class Profiler {
  private markers: Map<string, PerformanceMarker> = new Map();
  private enabled: boolean = true;

  constructor() {
    // Enable profiling in development or when explicitly enabled
    this.enabled = process.env.NODE_ENV !== 'production' || 
                   (typeof window !== 'undefined' && 
                    (window.location.search.includes('profile=true') || 
                     localStorage.getItem('enableProfiling') === 'true'));
  }

  /**
   * Start a performance marker
   */
  start(name: string, metadata?: Record<string, any>): void {
    if (!this.enabled) return;

    const marker: PerformanceMarker = {
      name,
      startTime: performance.now(),
      metadata,
    };

    this.markers.set(name, marker);

    // Also use browser Performance API
    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark(`${name}-start`);
    }
  }

  /**
   * End a performance marker and log the duration
   */
  end(name: string, additionalMetadata?: Record<string, any>): number | null {
    if (!this.enabled) return null;

    const marker = this.markers.get(name);
    if (!marker) {
      // Silently ignore if marker was already ended (prevent duplicate warnings)
      return null;
    }

    const endTime = performance.now();
    const duration = endTime - marker.startTime;

    marker.endTime = endTime;
    marker.duration = duration;
    if (additionalMetadata) {
      marker.metadata = { ...marker.metadata, ...additionalMetadata };
    }

    // Use browser Performance API
    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark(`${name}-end`);
      performance.measure(name, `${name}-start`, `${name}-end`);
    }

    // Log the result
    const emoji = duration > 1000 ? '🐌' : duration > 500 ? '⚡' : '✅';
    console.log(
      `[Profiler] ${emoji} ${name}: ${duration.toFixed(2)}ms`,
      marker.metadata ? marker.metadata : ''
    );

    this.markers.delete(name);
    return duration;
  }

  /**
   * Measure an async function
   */
  async measure<T>(
    name: string,
    fn: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    this.start(name, metadata);
    try {
      const result = await fn();
      this.end(name, { success: true });
      return result;
    } catch (error) {
      this.end(name, { success: false, error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  }

  /**
   * Measure a synchronous function
   */
  measureSync<T>(
    name: string,
    fn: () => T,
    metadata?: Record<string, any>
  ): T {
    this.start(name, metadata);
    try {
      const result = fn();
      this.end(name, { success: true });
      return result;
    } catch (error) {
      this.end(name, { success: false, error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  }

  /**
   * Get all active markers
   */
  getActiveMarkers(): PerformanceMarker[] {
    return Array.from(this.markers.values());
  }

  /**
   * Get a summary of all completed markers
   */
  getSummary(): Record<string, number> {
    const summary: Record<string, number> = {};
    // Note: This only includes markers that are still in the map
    // Completed markers are removed, so this shows active ones
    this.markers.forEach((marker, name) => {
      if (marker.duration) {
        summary[name] = marker.duration;
      }
    });
    return summary;
  }

  /**
   * Clear all markers
   */
  clear(): void {
    this.markers.clear();
  }

  /**
   * Enable or disable profiling
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if profiling is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }
}

// Export singleton instance
export const profiler = new Profiler();

// Export types
export type { PerformanceMarker };

